/**
 * Kaarteffect-resolutie (§3.1) + output-schaling.
 *
 * Output-schaling raakt de gevechts-outputs block/heal/dmg. Volgorde:
 *  nullify → ×mult(origin) → ×halfEffect(Type) → ×randomize → reverseNext(=0)
 *  → +buffNext/+buffOutput(matching) → −debuffOutput → afronden, ≥0.
 * Energie/trekken e.d. worden niet door buffs geschaald (bewuste keuze).
 */
import type { Kaart, KaartEffect } from '../data/types';
import type { BattleState } from './state';
import { log, rngNext, trek } from './state';
import { cleanse, heelSpeler, heeftStatus, schadeAanBaas, vindStatus } from './mutators';

/** Schaalt een numerieke gevechts-output voor de gegeven kaart. */
function schaal(state: BattleState, kaart: Kaart, basis: number): number {
  if (basis === 0) return 0;
  const sp = state.speler;

  // nullify
  if (heeftStatus(state, 'nullifyAll')) return 0;
  const nt = vindStatus(state, 'nullifyType');
  if (nt && nt.type === kaart.type) return 0;

  let mult = 1;
  for (const m of sp.multDeze) if (m.origin === kaart.origin) mult *= m.amt;
  if (heeftStatus(state, 'halfEffect')) mult *= 0.5;
  const ht = vindStatus(state, 'halfEffectType');
  if (ht && ht.type === kaart.type) mult *= 0.5;
  const rnd = vindStatus(state, 'randomize');
  if (rnd) mult *= 0.5 + rngNext(state.rng); // 0.5–1.5

  // reverseNext (one-shot): output → 0, status verbruiken
  const reverse = sp.statuses.find((s) => s.kind === 'reverseNext' && s.resterend > 0);
  if (reverse) {
    reverse.resterend = 0;
    log(state, 'Omgekeerde waarneming: kaart zonder effect.');
    return 0;
  }

  let add = sp.buffNext;
  for (const b of sp.buffOutput) if (!b.type || b.type === kaart.type) add += b.amt;
  const debuff = vindStatus(state, 'debuffOutput');
  const min = debuff?.amt ?? 0;

  const val = Math.max(0, Math.round(basis * mult) + add - min);
  return val;
}

/** Speelt het effect van een kaart (kaart is al uit de hand gehaald + betaald). */
export function speelKaartEffect(state: BattleState, kaart: Kaart): void {
  const e: KaartEffect = kaart.effect;
  const sp = state.speler;

  // BLOCK — dubbele rol: +schild én −baas-HP (tenzij noBossDmg).
  if (e.block) {
    const n = schaal(state, kaart, e.block);
    sp.schild += n;
    sp.schildTurns = Math.max(sp.schildTurns, e.blockTurns ?? 1);
    sp.blockDezeBeurt += n;
    if (state.baasState.baas.verbindingFight) {
      // Theluun: block stort rechtstreeks in Verbinding (§13).
      state.baasState.verbinding = Math.max(0, Math.min(100, state.baasState.verbinding + n));
    } else if (!heeftStatus(state, 'noBossDmg')) {
      schadeAanBaas(state, n);
    }
  }

  if (e.full) sp.volledigBlok = true;
  if (e.heal) heelSpeler(state, schaal(state, kaart, e.heal));
  if (e.healOverTime) sp.healOverTime.push({ amt: e.healOverTime.amt, resterend: e.healOverTime.turns });
  if (e.dmg) schadeAanBaas(state, schaal(state, kaart, e.dmg));

  if (e.energy) sp.energie = Math.max(0, sp.energie + e.energy);
  if (e.energyNext) sp.energieVolgende += e.energyNext;
  if (e.draw) trek(state, e.draw);

  if (e.drainBoss) {
    state.baasState.drainAmt = Math.max(state.baasState.drainAmt, e.drainBoss.amt);
    state.baasState.drainTurns = Math.max(state.baasState.drainTurns, e.drainBoss.turns);
  }
  if (e.weakenBoss) {
    state.baasState.weakenAmt = Math.max(state.baasState.weakenAmt, e.weakenBoss.amt);
    state.baasState.weakenTurns = Math.max(state.baasState.weakenTurns, e.weakenBoss.turns);
  }

  if (e.costCut) sp.costCutDeze += e.costCut;
  if (e.costDownType) sp.costDownType.push({ type: e.costDownType.type, amt: e.costDownType.amt, resterend: e.costDownType.turns });
  if (e.buffNext) sp.buffNext += e.buffNext;
  if (e.buffOutput) sp.buffOutput.push({ amt: e.buffOutput.amt, resterend: e.buffOutput.turns });
  if (e.mult) sp.multDeze.push({ origin: e.mult.origin, amt: e.mult.amt });

  if (e.cleanse) cleanse(state, e.cleanse);
  if (e.removeAtk) {
    const bs = state.baasState;
    const besch = bs.baas.aanvallen.filter((a) => !bs.verwijderd.includes(a.naam));
    if (besch.length) {
      bs.verwijderd.push(besch[Math.floor(rngNext(state.rng) * besch.length)].naam);
      log(state, 'Baas verliest een aanval.');
    }
  }
  if (e.reflectNext) sp.reflectVolgende = true;
  if (e.unfreeze) {
    if (state.bevroren.length) log(state, 'Bevroren kaarten ontdooid.');
    state.bevroren = [];
  }

  if (e.aura) sp.aura = { ...e.aura };
  if (e.clearVeilPermanent) {
    sp.veilPermanentCleared = true;
    sp.statuses = sp.statuses.filter((s) => s.kind !== 'veil');
  }
  if (e.veilImmuneTurns) sp.veilImmuneTurns = Math.max(sp.veilImmuneTurns, e.veilImmuneTurns);

  if (e.freeAll) sp.freeAll = true;
  if (e.freeType) { if (!sp.freeTypes.includes(e.freeType.type)) sp.freeTypes.push(e.freeType.type); }
  if (e.freeNextCount) sp.freeNextCount += e.freeNextCount;

  if (e.bossSkip) state.baasState.slaOver = true;
  if (e.peek && state.deck.length) {
    const top = state.deck.slice(0, e.peek).map((i) => i.kaart.naam).join(', ');
    log(state, `Bovenste kaarten: ${top}.`);
  }
  if (e.tone) state.tonen.push(e.tone);
}
